import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import TextareaAutosize from 'react-autosize-textarea'
import { listPipe } from '@aytm/lib/common/func'

import { PureDataComponent } from '@aytm/lib/common/components/pure-component'

import MaskedInput from '@aytm/lib/rfd-ui/components/basic/masked-input'

import '@aytm/lib/rfd-ui/styles/basic/text-field.scss'

export default class TextField extends PureDataComponent {
  static propTypes = {
    value: PropTypes.string,
    defaultValue: PropTypes.string,
    placeholder: PropTypes.string,
    inputType: PropTypes.string,
    autoFocus: PropTypes.bool,
    readOnly: PropTypes.bool,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    debounce: PropTypes.number,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    active: PropTypes.bool,
    error: PropTypes.bool,
    icon: PropTypes.string,
    iconColor: PropTypes.string,
    onIconClick: PropTypes.func,
    charLimit: PropTypes.number,
    counterWidth: PropTypes.number,
    prefix: PropTypes.string,
    prefixWidth: PropTypes.number,
    filter: PropTypes.func,
    format: PropTypes.func,
    rows: PropTypes.number,
    theme: PropTypes.string,
    // masking params      :
    maskPattern: PropTypes.string,
    maskPlaceholderChar: PropTypes.string,
    maskFormatCharacters: PropTypes.object,
    maskSub: PropTypes.string
  }

  constructor(props) {
    super(props)
    this.onFocus = this.onFocus.bind(this);
    this.focus = this.focus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onMaskedChange = this.onMaskedChange.bind(this);
    this.sendUpdate = this.sendUpdate.bind(this);
    this.state = {
      focused: false,
      value: ''
    }
    this.debounceTimeout = null
    this.debouncePrevValue = null
  }

  onFocus(event) {
    const { onFocus } = this.props
    if ('function' == typeof onFocus) onFocus(event)
    this.setState({ focused: true })
  }

  focus() {
    if (this.refInput) {
      this.refInput.focus()
      return true
    }
    return false
  }

  onBlur(event) {
    const { onBlur } = this.props
    const newValue = listPipe(
      [
        v => {
          const { format } = this.props
          return 'function' == typeof format ? format(v) || '' : v
        }
      ],
      ((this.refInput instanceof MaskedInput ? this.refInput.refInput : this.refInput) || event.target).value || ''
    )
    if ('function' == typeof this.props.onBlur) onBlur(event)
    const { value: prevValue } = this.state
    this.setState(
      {
        value: newValue,
        focused: false
      },
      () => {
        this.sendUpdate(newValue, prevValue, null, false)
      }
    )
  }

  onChange(event) {
    // const { resizable } = this.props
    const newValue = listPipe(
      [
        v => {
          const { charLimit } = this.props
          return null != charLimit && v.length > charLimit ? v.slice(0, Math.max(0, charLimit)) : v
        },
        v => {
          const { filter } = this.props
          return 'function' == typeof filter ? filter(v) || '' : v
        }
      ],
      (this.refInput || event.target).value || ''
    )
    const { value: prevValue } = this.state
    // console.log(this.refInput.height)
    // if (resizable && this.refInput.cols) {
    //   const rows = Math.max(this.props.rows, this.refInput.textLength / this.refInput.cols)
    //   if (rows != this.state.rows)
    //     this.setState({ rows: rows })
    // }
    this.setState({ value: newValue }, () => {
      this.sendUpdate(newValue, prevValue)
    })
    return { newValue, prevValue }
  }

  onMaskedChange(newValue, prevValue, data) {
    return this.sendUpdate(newValue, prevValue, data)
  }

  sendUpdate(newValue, prevValue, data, debounce = this.props.debounce) {
    const { onChange } = this.props
    if (newValue != prevValue && 'function' == typeof onChange) {
      if (debounce) {
        if (this.debounceTimeout) clearTimeout(this.debounceTimeout)
        this.debounceTimeout = setTimeout(() => {
          if (this.mounted) onChange(newValue, prevValue, data)
          this.debounceTimeout = null
        }, debounce)
      } else if (this.mounted) {
        onChange(newValue, prevValue, data)
      }
    }
    return { newValue, prevValue, data }
  }

  componentDidMount() {
    super.componentDidMount()
    const { value, defaultValue, format, autoFocus } = this.props
    const initValue = value || defaultValue
    if (initValue) {
      this.setState({
        value: 'function' == typeof format ? this.props.format(initValue) : initValue
      })
    }
    if (autoFocus) this.focus()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value && this.props.value !== this.state.value) {
      this.setState({ value: this.props.value || '' })
    }
  }

  //TODO: consider breaking into subrenders
  render() {
    const {
      width,
      icon,
      iconColor,
      active,
      error,
      style,
      className,
      placeholder,
      readOnly,
      maskPattern,
      maskPlaceholderChar,
      maskFormatCharacters,
      maskSub,
      onIconClick,
      onFocus,
      onBlur,
      onChange,
      debounce,
      value,
      defaultValue,
      charLimit,
      prefix,
      postfix,
      theme,
      format,
      filter,
      counterWidth,
      prefixWidth,
      postfixWidth,
      cols,
      rows,
      autoFocus,
      resizableHeight,
      inputType = 'text',
      ...otherProps
    } = this.props

    const { focused, value: sValue } = this.state
    const innerPaddingRight = listPipe(
      [
        p => (icon ? (p || 0) + 33 : p),
        p => (postfix ? (null == postfixWidth ? p + 15 : p + postfixWidth) : p),
        p =>
          !maskPattern && charLimit
            ? (p || 0) + (counterWidth ? counterWidth || 33 : Number(String(charLimit).length))
            : p
      ],
      null
    )
    const innerPaddingLeft = prefix ? (null == prefixWidth ? 26 : prefixWidth + 16) : null
    // if (autoFocus) this.focus()

    return (
      <div
        className={classNames('rfd-ui-text-field', className, {
          'rfd-ui-text-field-active': active,
          'rfd-ui-text-field-error': error,
          'rfd-ui-text-field-focused': focused,
          'rfd-ui-text-field-readonly': readOnly,
          'rfd-ui-text-field-area': !maskPattern && rows,
          [`rfd-ui-text-field-${theme}`]: theme
        })}
        style={{ width }}
      >
        <div
          className="rfd-ui-text-field-inner"
          style={{
            padding: !maskPattern && rows ? 0 : 'small' == theme ? 3 : 7,
            paddingRight: !maskPattern && rows ? 0 : innerPaddingRight,
            paddingLeft: !maskPattern && rows ? 0 : innerPaddingLeft
          }}
        >
          {icon && (
            <i
              className={`rfd-ui-text-field-icon ${icon}`}
              style={{
                color: iconColor,
                cursor: onIconClick ? 'pointer' : null
              }}
              onClick={onIconClick}
            />
          )}
          {prefix && (
            <span className="rfd-ui-text-field-prefix" style={{ width: prefixWidth }}>
              {prefix}
            </span>
          )}
          {!maskPattern && charLimit && (
            <span
              className="rfd-ui-text-field-counter"
              style={{
                right: icon ? 33 : rows ? 16 : 7,
                width: counterWidth ? String(charLimit).length * 10 : String(charLimit).length * 10
              }}
            >
              {charLimit - sValue.length}
            </span>
          )}
          {!(maskPattern || rows) && (
            <input
              className={`rfd-ui-text-field-input ${postfix ? 'right' : ''}`}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              onChange={this.onChange}
              value={sValue}
              readOnly={readOnly}
              placeholder={placeholder}
              ref={this.saveRef('Input')}
              type={inputType}
              {...otherProps}
            />
          )}
          {!maskPattern && rows && resizableHeight && (
            <TextareaAutosize
              autoFocus={autoFocus}
              className="rfd-ui-text-area-field"
              cols={cols ? cols : width ? Math.abs(width / 15) : null}
              rows={rows}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              onChange={this.onChange}
              value={sValue}
              readOnly={readOnly}
              placeholder={placeholder}
              innerRef={this.saveRef('Input')}
              type="text"
              style={{ paddingRight: innerPaddingRight + 8, paddingLeft: innerPaddingLeft }}
              {...otherProps}
            />
          )}
          {maskPattern && (
            <MaskedInput
              className="rfd-ui-text-field-input rfd-ui-text-field-masked-input"
              pattern={maskPattern}
              placeholderChar={maskPlaceholderChar}
              formatCharacters={maskFormatCharacters}
              sub={maskSub}
              width={width ? width : 200}
              value={sValue}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              onChange={this.onMaskedChange}
              ref={this.saveRef('Input')}
              {...otherProps}
            />
          )}
          {postfix && (
            <span className="rfd-ui-text-field-postfix" style={{ width: postfixWidth }}>
              {postfix}
            </span>
          )}
        </div>
      </div>
    )
  }
}
